/* =====================================================================
   CART GIFT TIERS v2.1.2 — Medusafilters
   HOTFIX-BUILD: 2026-04-11
   Fixes gegenüber v2.1.1:
     - Guardrail läuft jetzt IMMER mit frischem /cart.js (keine stale snapshots)
     - Self-healing _isProcessing Lock (auto-reset nach 8 s)
     - Separater Lock für Remove-Button vs. Guardrail
     - cart:change dispatch in der Shape, die Impact erwartet:
         { cart, quantity, sections } auf document
     - Zusätzlich cart:refresh als Fallback → Impact rendert Drawer sauber
     - Debug-Logging via  window.__CGT_DEBUG = true
     - Guardrail-Re-Trigger 200 ms nach _handleAdd.finally
     - Periodischer Safety-Net Check alle 5 s wenn Drawer offen
   ===================================================================== */

(function () {
  const LOG = (...args) => {
    if (window.__CGT_DEBUG) console.log('[CGT]', ...args);
  };
  const WARN = (...args) => console.warn('[CGT]', ...args);

  // Visible build marker → Karim sieht in DevTools-Console, welche Version läuft
  console.log('%c[CGT] build v2.8.0 loaded (auto-add + auto-remove, subtotal=original_line_price)', 'color:#8E63F8;font-weight:bold;');

  // ------------------------------------------------------------------
  //  CROSS-INSTANCE LOCK (2026-06-16)
  //  Seit cart-gift-tiers auch auf der dedizierten Cart-Page (/cart)
  //  gerendert wird, existieren pro Seite ZWEI Instanzen: eine im global
  //  eingebundenen <cart-drawer> und eine in <main> (main-cart.liquid).
  //  Damit NIE beide gleichzeitig den Warenkorb mutieren (kein
  //  Doppel-Geschenk, keine doppelten Add-/Remove-Ketten, keine doppelten
  //  /cart.js-Guardrail-Laeufe), liegt der Lock window-global — geteilt
  //  ueber ALLE Instanzen UND ueber eine evtl. doppelte Script-Ausfuehrung
  //  (das Snippet wird zweimal gerendert → <script> laeuft zweimal).
  // ------------------------------------------------------------------
  const SHARED = (window.__cgtSharedLock = window.__cgtSharedLock || { lock: null, timer: null, failed: new Set() });
  if (!SHARED.failed) SHARED.failed = new Set();

  class CartGiftTiers extends HTMLElement {
    constructor() {
      super();
      // Lock liegt jetzt window-global (SHARED, siehe oben) — keine Instanz-Locks mehr.
      this._lastSubtotal = -1;
      this._confettiLib = null;
      this._boundOnCartChanged = this._onCartChanged.bind(this);
      this._boundOnClick = this._onClick.bind(this);
      this._boundOnDocClick = this._onDocClick.bind(this);
    }

    /* ------------------------------------------------------------------ */
    /*  SELF-HEALING LOCK                                                 */
    /* ------------------------------------------------------------------ */
    _acquireLock(owner) {
      if (SHARED.lock) {
        const age = Date.now() - SHARED.lock.t0;
        if (age < 8000) {
          LOG('lock busy', SHARED.lock.owner, 'requesting', owner);
          return false;
        }
        WARN('lock expired, force release from', SHARED.lock.owner);
      }
      SHARED.lock = { owner, t0: Date.now(), inst: this };
      // Auto-release nach 8 s — kein Permanent-Stuck mehr
      if (SHARED.timer) clearTimeout(SHARED.timer);
      SHARED.timer = setTimeout(() => {
        if (SHARED.lock && SHARED.lock.owner === owner) {
          WARN('lock auto-released (timeout)', owner);
          SHARED.lock = null;
        }
      }, 8000);
      LOG('lock acquired by', owner);
      return true;
    }

    _releaseLock(owner) {
      if (SHARED.lock && SHARED.lock.owner === owner) {
        SHARED.lock = null;
        if (SHARED.timer) { clearTimeout(SHARED.timer); SHARED.timer = null; }
        LOG('lock released by', owner);
      }
    }

    _isLocked() { return SHARED.lock !== null; }

    /* ------------------------------------------------------------------ */
    /*  CONNECT                                                           */
    /* ------------------------------------------------------------------ */
    connectedCallback() {
      // Anti-Dup: mehrere Instanzen in derselben Cart-Surface → nur erste behalten
      const parentCart = this.closest('cart-drawer, .cart-form, main, body');
      if (parentCart) {
        const siblings = parentCart.querySelectorAll('cart-gift-tiers');
        if (siblings.length > 1) {
          for (let i = 1; i < siblings.length; i++) {
            if (siblings[i] === this) { this.remove(); return; }
          }
        }
      }

      this._lastSubtotal = parseInt(this.dataset.subtotalCents || '0', 10);

      document.addEventListener('cart:change', this._boundOnCartChanged);
      document.addEventListener('cart:refresh', this._boundOnCartChanged);
      document.addEventListener('cart:updated', this._boundOnCartChanged);
      this.addEventListener('click', this._boundOnClick);
      document.addEventListener('click', this._boundOnDocClick, true);

      this._boundFocusCheck = () => this._runGuardrail();
      window.addEventListener('focus', this._boundFocusCheck);

      this._attachCheckoutLoader();
      this._runGuardrail();

      // Periodic safety net — alle 5 s prüfen, solange Drawer offen ist
      this._safetyInterval = setInterval(() => {
        const drawer = document.getElementById('cart-drawer');
        if (drawer && drawer.hasAttribute('open')) {
          this._runGuardrail();
        }
      }, 5000);

      this._ensureStickyMount();
      this._boundStickyMount = () => this._ensureStickyMount();
      document.addEventListener('cart:change', this._boundStickyMount);
      document.addEventListener('cart:refresh', this._boundStickyMount);
      document.addEventListener('cart:updated', this._boundStickyMount);
      document.addEventListener('dialog:open', this._boundStickyMount);
      document.addEventListener('cart-drawer:open', this._boundStickyMount);

      // v2.1.4: Cross-Sell Nav (hover arrows) init + re-init bei cart-events
      this._boundInitCrossNav = () => {
        try { this._initCrossNav(); } catch (e) { WARN('initCrossNav error', e); }
      };
      this._boundInitCrossNav();
      document.addEventListener('cart:change', this._boundInitCrossNav);
      document.addEventListener('cart:refresh', this._boundInitCrossNav);
      document.addEventListener('cart:updated', this._boundInitCrossNav);
      document.addEventListener('dialog:open', this._boundInitCrossNav);
      document.addEventListener('cart-drawer:open', this._boundInitCrossNav);

      LOG('connected', {
        g1: this.dataset.g1Handle, g2: this.dataset.g2Handle, g3: this.dataset.g3Handle,
        gifts: this.dataset.giftHandles, subtotal: this._lastSubtotal,
      });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._boundOnCartChanged);
      document.removeEventListener('cart:refresh', this._boundOnCartChanged);
      document.removeEventListener('cart:updated', this._boundOnCartChanged);
      document.removeEventListener('click', this._boundOnDocClick, true);
      window.removeEventListener('focus', this._boundFocusCheck);
      this.removeEventListener('click', this._boundOnClick);
      if (this._safetyInterval) clearInterval(this._safetyInterval);
      // Falls DIESE Instanz gerade den globalen Lock haelt, sauber freigeben,
      // damit eine verbleibende Instanz nicht bis zum 8 s-Auto-Release wartet.
      if (SHARED.lock && SHARED.lock.inst === this) {
        SHARED.lock = null;
        if (SHARED.timer) { clearTimeout(SHARED.timer); SHARED.timer = null; }
      }
      if (this._boundStickyMount) {
        document.removeEventListener('cart:change', this._boundStickyMount);
        document.removeEventListener('cart:refresh', this._boundStickyMount);
        document.removeEventListener('cart:updated', this._boundStickyMount);
        document.removeEventListener('dialog:open', this._boundStickyMount);
        document.removeEventListener('cart-drawer:open', this._boundStickyMount);
      }
      if (this._boundInitCrossNav) {
        document.removeEventListener('cart:change', this._boundInitCrossNav);
        document.removeEventListener('cart:refresh', this._boundInitCrossNav);
        document.removeEventListener('cart:updated', this._boundInitCrossNav);
        document.removeEventListener('dialog:open', this._boundInitCrossNav);
        document.removeEventListener('cart-drawer:open', this._boundInitCrossNav);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  STICKY MOUNT                                                      */
    /* ------------------------------------------------------------------ */
    _ensureStickyMount() {
      const scrollHost = this._findScrollContainer();
      if (!scrollHost) return;
      scrollHost.setAttribute('data-cgt-sticky-host', '1');
      let p = this.parentElement;
      while (p && p !== document.body) {
        const cs = getComputedStyle(p);
        if (cs.transform !== 'none' || cs.filter !== 'none' || cs.contain.includes('paint')) {
          p.style.transform = 'none';
          p.style.filter = 'none';
          p.style.contain = 'none';
        }
        if (p === scrollHost) break;
        p = p.parentElement;
      }
      this.style.position = 'sticky';
      this.style.top = '0px';
      this.style.zIndex = '50';
      this._activateScrollPinFallback(scrollHost);
    }

    _findScrollContainer() {
      let p = this.parentElement;
      let fallback = null;
      while (p && p !== document.body) {
        const cs = getComputedStyle(p);
        const oy = cs.overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 2) return p;
        if ((oy === 'auto' || oy === 'scroll') && !fallback) fallback = p;
        p = p.parentElement;
      }
      return fallback;
    }

    _activateScrollPinFallback(scrollHost) {
      if (this._pinScrollHandler && this._pinScrollHost) {
        this._pinScrollHost.removeEventListener('scroll', this._pinScrollHandler);
      }
      this._pinScrollHost = scrollHost;
      const parent = this.parentElement;
      if (parent) {
        const ps = getComputedStyle(parent);
        if (ps.position === 'static') parent.style.position = 'relative';
      }
      if (!this._pinPlaceholder) {
        this._pinPlaceholder = document.createElement('div');
        this._pinPlaceholder.setAttribute('data-cgt-placeholder', '');
        this._pinPlaceholder.style.cssText = 'display:none;width:100%;';
        this.parentNode.insertBefore(this._pinPlaceholder, this);
      }
      const computeOrigTop = () => {
        const hostRect = scrollHost.getBoundingClientRect();
        const selfRect = this.getBoundingClientRect();
        return selfRect.top - hostRect.top + scrollHost.scrollTop;
      };
      let origTop = computeOrigTop();
      const pin = () => {
        if (this._isPinned) return;
        this._isPinned = true;
        this._pinPlaceholder.style.height = this.offsetHeight + 'px';
        this._pinPlaceholder.style.display = 'block';
        this.style.position = 'absolute';
        this.style.top = scrollHost.scrollTop + 'px';
        this.style.left = '0';
        this.style.right = '0';
        this.style.width = '100%';
        this.style.zIndex = '50';
      };
      const unpin = () => {
        if (!this._isPinned) return;
        this._isPinned = false;
        this._pinPlaceholder.style.display = 'none';
        this.style.position = 'sticky';
        this.style.top = '0px';
        this.style.left = '';
        this.style.right = '';
        this.style.width = '';
      };
      const onScroll = () => {
        const scrollTop = scrollHost.scrollTop;
        if (scrollTop >= origTop - 1) {
          if (!this._isPinned) pin();
          else this.style.top = scrollTop + 'px';
        } else if (this._isPinned) unpin();
      };
      const recompute = () => {
        if (this._isPinned) unpin();
        requestAnimationFrame(() => { origTop = computeOrigTop(); onScroll(); });
      };
      scrollHost.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', recompute);
      this._pinScrollHandler = onScroll;
      requestAnimationFrame(() => { origTop = computeOrigTop(); onScroll(); });
    }

    /* ------------------------------------------------------------------ */
    /*  GIFT REMOVE                                                       */
    /* ------------------------------------------------------------------ */
    _onDocClick(event) {
      // 1) Gift-Remove (Trash auf einem Gift-Line-Item)
      const removeBtn = event.target.closest('[data-cgt-gift-remove]');
      if (removeBtn) {
        event.preventDefault();
        event.stopPropagation();
        this._handleGiftRemove(removeBtn);
        return;
      }
      // 2) Cross-Sell-Add (v2.1.4 — neuer "+" Button in cart-cross-sell)
      const crossBtn = event.target.closest('[data-cart-cross-add]');
      if (crossBtn) {
        event.preventDefault();
        event.stopPropagation();
        this._handleCrossAdd(crossBtn);
        return;
      }
      // 3) Cross-Sell-Scroll-Navigation (Hover-Arrows)
      const navBtn = event.target.closest('[data-cgt-cross-nav]');
      if (navBtn) {
        event.preventDefault();
        this._handleCrossNav(navBtn);
        return;
      }
      // 4) Empty-State-Scroll-Navigation (Hover-Arrows)
      const emptyNavBtn = event.target.closest('[data-cgt-empty-nav]');
      if (emptyNavBtn) {
        event.preventDefault();
        this._handleEmptyNav(emptyNavBtn);
        return;
      }
      // 5) Gift-Tiles-Scroll-Navigation (v2.1.5+)
      const tilesNavBtn = event.target.closest('[data-cgt-tiles-nav]');
      if (tilesNavBtn) {
        event.preventDefault();
        this._handleTilesNav(tilesNavBtn);
        return;
      }
    }

    _handleGiftRemove(btn) {
      const lineKey = btn.getAttribute('data-line-key');
      if (!lineKey) { WARN('remove: no lineKey'); return; }
      if (!this._acquireLock('remove')) return;

      LOG('removing gift', lineKey);
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('is-loading');
      // v2.5.9 HOTFIX: KEIN _setCheckoutLoading mehr — führte zu hängendem
      // disabled auf dem Checkout-Button wegen Race mit Impact-Rerender.

      const sections = this._getBundledSections();
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey, quantity: 0, sections: sections, sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((cart) => {
          this._lastSubtotal = this._computeQualifyingSubtotal(cart);
          this._dispatchImpactCartChange(cart, 0);
          LOG('gift removed, new subtotal', this._lastSubtotal);
        })
        .catch((e) => WARN('remove error', e))
        .finally(() => {
          this._releaseLock('remove');
          if (btn && btn.isConnected) {
            btn.removeAttribute('disabled');
            btn.classList.remove('is-loading');
          }
          // v2.5.9 HOTFIX: _setCheckoutLoading(false) entfernt
          // Guardrail nochmal für sicher — falls andere Gifts noch weg müssen
          setTimeout(() => this._runGuardrail(), 200);
        });
    }

    /* ------------------------------------------------------------------ */
    /*  CART EVENT LISTENER                                               */
    /* ------------------------------------------------------------------ */
    _onCartChanged(event) {
      // NICHT mehr early-return bei Lock — Guardrail kümmert sich selbst
      const cart = event && event.detail && event.detail.cart;
      LOG('cart event', event && event.type, 'has cart:', !!cart, 'items:', cart && cart.items && cart.items.length);

      if (cart && Array.isArray(cart.items)) {
        const newSubtotal = this._computeQualifyingSubtotal(cart);
        const oldSubtotal = this._lastSubtotal;
        const thresholds = this._getAllThresholds();
        let crossedUp = false;
        for (const t of thresholds) {
          if (t > 0 && oldSubtotal < t && newSubtotal >= t) { crossedUp = true; break; }
        }
        this._lastSubtotal = newSubtotal;
        if (crossedUp) this._fireConfetti();
      }
      // Guardrail läuft IMMER mit frischem /cart.js, unabhängig vom event cart
      this._runGuardrail();
    }

    /* ------------------------------------------------------------------ */
    /*  QUALIFYING SUBTOTAL + GIFT DETECTION                              */
    /* ------------------------------------------------------------------ */
    /* BEZUGSGROESSE (2026-08-08, Freigabe Karim): original_line_price = Warenwert
       VOR Rabatt. Muss identisch zu cart-gift-tiers.liquid bleiben (dort dieselbe
       Umstellung mit ausfuehrlicher Begruendung), sonst rechnen Server-Render und
       Auto-Add unterschiedlich und Geschenke werden zugefuegt/entfernt geflippt.
       Grund: die Sale-Rabattregel prueft den Warenwert vor Rabatt; mit
       final_line_price fiel der gemessene Betrag genau dann unter die Schwelle,
       wenn der Rabatt griff. */
    _computeQualifyingSubtotal(cart) {
      const giftHandles = (this.dataset.giftHandles || '').split(',').filter(Boolean);
      let subtotal = 0;
      (cart.items || []).forEach((item) => {
        if (!this._isGift(item, giftHandles)) {
          const line = (item.original_line_price != null) ? item.original_line_price : item.final_line_price;
          subtotal += line || 0;
        }
      });
      return Math.max(0, subtotal);
    }

    _isGift(item, giftHandles) {
      if (!item || !item.properties || item.properties._gift !== 'true') return false;
      if (!giftHandles || giftHandles.length === 0) return false;
      return giftHandles.includes(item.handle);
    }

    _getGiftThresholds() {
      return [
        parseInt(this.dataset.g1Threshold || '0', 10),
        parseInt(this.dataset.g2Threshold || '0', 10),
        parseInt(this.dataset.g3Threshold || '0', 10),
      ];
    }

    _getAllThresholds() {
      const gifts = this._getGiftThresholds();
      const ship = parseInt(this.dataset.shippingThreshold || '0', 10);
      return [...gifts, ship];
    }

    /* ------------------------------------------------------------------ */
    /*  GUARDRAIL — fetcht IMMER frischen /cart.js                        */
    /* ------------------------------------------------------------------ */
    _runGuardrail() {
      if (!this._acquireLock('guardrail')) return;

      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          const subtotal = this._computeQualifyingSubtotal(cart);
          const gifts = this._getGiftThresholds();
          const giftHandles = (this.dataset.giftHandles || '').split(',').filter(Boolean);
          const g1Handle = this.dataset.g1Handle;
          const g2Handle = this.dataset.g2Handle;
          const g3Handle = this.dataset.g3Handle;

          LOG('guardrail run', {
            subtotal, gifts, items: (cart.items || []).length,
          });

          const toRemove = [];
          (cart.items || []).forEach((item) => {
            if (!this._isGift(item, giftHandles)) return;
            let belowThreshold = false;
            if (item.handle === g1Handle && subtotal < gifts[0]) belowThreshold = true;
            else if (item.handle === g2Handle && subtotal < gifts[1]) belowThreshold = true;
            else if (item.handle === g3Handle && subtotal < gifts[2]) belowThreshold = true;

            if (belowThreshold) {
              LOG('to remove (below threshold)', item.handle, item.key);
              toRemove.push({ key: item.key, quantity: 0, reason: 'lost-threshold' });
              return;
            }
            if (item.quantity > 1) {
              LOG('to normalize (qty>1)', item.handle, item.key);
              toRemove.push({ key: item.key, quantity: 1, reason: 'normalize' });
            }
          });

          // Entfernen hat Vorrang: nach dem Remove laeuft die Guardrail
          // ohnehin erneut und ergaenzt dann fehlende Geschenke.
          if (toRemove.length > 0) {
            this._chainRemove(toRemove, 0);
            return;
          }

          const toAdd = this._collectMissingGifts(cart, subtotal);
          if (toAdd.length > 0) {
            LOG('auto-add', toAdd.map((t) => t.handle));
            this._chainAdd(toAdd, 0);
            return;
          }

          LOG('guardrail: nothing to do');
          this._releaseLock('guardrail');
        })
        .catch((e) => {
          WARN('guardrail error', e);
          this._releaseLock('guardrail');
        });
    }

    /* ------------------------------------------------------------------ */
    /*  AUTO-ADD (2026-08-06)                                             */
    /*  Geschenke werden ohne Klick zugefuegt, sobald die Schwelle steht.  */
    /*  Praesenz wird ueber den HANDLE geprueft, nicht ueber die           */
    /*  _gift-Property: so entsteht auch dann kein Doppel-Artikel, wenn    */
    /*  dasselbe Produkt ohne Property im Warenkorb liegt.                 */
    /*  Kein Endlos-Retry: ein fehlgeschlagener Add (z. B. ausverkauft)    */
    /*  landet in SHARED.failed und wird erst wieder versucht, nachdem     */
    /*  die Schwelle zwischenzeitlich unterschritten war.                  */
    /* ------------------------------------------------------------------ */
    _collectMissingGifts(cart, subtotal) {
      const gifts = this._getGiftThresholds();
      const inCart = new Set((cart.items || []).map((i) => i.handle));
      const tiers = [
        { handle: this.dataset.g1Handle, variantId: this.dataset.g1VariantId, threshold: gifts[0] },
        { handle: this.dataset.g2Handle, variantId: this.dataset.g2VariantId, threshold: gifts[1] },
        { handle: this.dataset.g3Handle, variantId: this.dataset.g3VariantId, threshold: gifts[2] },
      ];
      const missing = [];
      tiers.forEach((t) => {
        if (!t.handle || !t.variantId || !(t.threshold > 0)) return;
        if (subtotal < t.threshold) {
          // Schwelle wieder verlassen → Fehlversuch verfaellt
          SHARED.failed.delete(t.handle);
          return;
        }
        if (inCart.has(t.handle)) return;
        if (SHARED.failed.has(t.handle)) return;
        missing.push(t);
      });
      return missing;
    }

    _chainAdd(list, idx) {
      if (idx >= list.length) {
        LOG('chainAdd complete');
        this._releaseLock('guardrail');
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            this._lastSubtotal = this._computeQualifyingSubtotal(cart);
            this._dispatchImpactCartChange(cart, 1);
          })
          .catch(() => document.dispatchEvent(new CustomEvent('cart:refresh')));
        return;
      }
      const tier = list[idx];
      LOG('chainAdd step', idx, tier.handle);
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(tier.variantId, 10), quantity: 1, properties: { _gift: 'true' } }],
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
        .then(() => this._chainAdd(list, idx + 1))
        .catch((e) => {
          WARN('auto-add fehlgeschlagen, Stufe uebersprungen', tier.handle, e);
          SHARED.failed.add(tier.handle);
          this._chainAdd(list, idx + 1);
        });
    }

    _chainRemove(list, idx, lastCart) {
      if (idx >= list.length) {
        LOG('chainRemove complete, dispatching final cart state');
        this._releaseLock('guardrail');
        // HOTFIX v2.1.3: Den letzten /cart/change.js-Response direkt
        // verwenden — der enthaelt bereits `sections` und kann safe an
        // Impact's cart:change-Handler uebergeben werden. Kein zusaetzlicher
        // /cart.js-Fetch mehr (der crashte Impact wegen fehlender sections).
        if (lastCart) {
          this._dispatchImpactCartChange(lastCart, 0);
        } else {
          // Nichts zu dispatchen → nur safe refresh
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        return;
      }
      const { key, quantity, reason } = list[idx];
      LOG('chainRemove step', idx, key, quantity, reason);
      this._changeLineItem(key, quantity, false)
        .then((cartResponse) => this._chainRemove(list, idx + 1, cartResponse))
        .catch((e) => {
          WARN('chainRemove step failed', e);
          this._releaseLock('guardrail');
        });
    }

    /* ------------------------------------------------------------------ */
    /*  ADD (on "+" click)                                                */
    /* ------------------------------------------------------------------ */
    _onClick(event) {
      const addBtn = event.target.closest('[data-cart-gift-add]');
      if (addBtn) {
        event.preventDefault();
        this._handleAdd(addBtn);
      }
    }

    _handleAdd(btn) {
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      if (!this._acquireLock('add')) return;

      LOG('adding gift', variantId);
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('is-loading');
      // v2.5.9 HOTFIX: KEIN _setCheckoutLoading mehr

      const sections = this._getBundledSections();
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: { _gift: 'true' },
          }],
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((response) => {
          // Fetch fresh /cart.js so we have items + item_count for Impact
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then((r) => r.json())
            .then((cart) => {
              this._lastSubtotal = this._computeQualifyingSubtotal(cart);
              // Merge sections aus add-response in cart-object (Impact nutzt sections)
              if (response && response.sections) {
                cart.sections = response.sections;
              }
              LOG('add success, dispatching cart:change', {
                item_count: cart.item_count, subtotal: this._lastSubtotal,
              });
              this._dispatchImpactCartChange(cart, 1);
            });
        })
        .catch((e) => WARN('add error', e))
        .finally(() => {
          this._releaseLock('add');
          if (btn && btn.isConnected) {
            btn.removeAttribute('disabled');
            btn.classList.remove('is-loading');
          }
          // v2.5.9 HOTFIX: _setCheckoutLoading(false) entfernt
        });
    }

    /* ------------------------------------------------------------------ */
    /*  CROSS-SELL ADD (v2.1.4)                                           */
    /*  Direkter Add mit first-available-variant, ohne _gift-Property.    */
    /*  Nutzt denselben Self-Healing-Lock wie _handleAdd.                 */
    /* ------------------------------------------------------------------ */
    _handleCrossAdd(btn) {
      const variantId = btn.getAttribute('data-variant-id');
      const handle = btn.getAttribute('data-product-handle') || '';
      if (!variantId) { WARN('crossAdd: no variantId'); return; }
      if (!this._acquireLock('crossAdd')) return;

      LOG('crossAdd start', { variantId, handle });
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('is-loading');
      // v2.5.9 HOTFIX: KEIN _setCheckoutLoading mehr

      const sections = this._getBundledSections();
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
          }],
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((response) => {
          // Holen frischen Cart inkl. item_count fuer Impact
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then((r) => r.json())
            .then((cart) => {
              if (response && response.sections) {
                cart.sections = response.sections;
              }
              LOG('crossAdd success', { item_count: cart.item_count });
              this._dispatchImpactCartChange(cart, 1);
            });
        })
        .catch((e) => WARN('crossAdd error', e))
        .finally(() => {
          this._releaseLock('crossAdd');
          if (btn && btn.isConnected) {
            btn.removeAttribute('disabled');
            btn.classList.remove('is-loading');
          }
          // v2.5.9 HOTFIX: _setCheckoutLoading(false) entfernt
        });
    }

    /* ------------------------------------------------------------------ */
    /*  CROSS-SELL NAV ARROWS (v2.1.4)                                    */
    /*  Hover-Arrows scrollen den naechsten Card-Schritt hinein.          */
    /* ------------------------------------------------------------------ */
    _handleCrossNav(btn) {
      const direction = btn.getAttribute('data-cgt-cross-nav');
      // Scroll-Container ist der naechste Nachbar im Viewport
      const viewport = btn.closest('.cgt-cross__viewport');
      if (!viewport) return;
      const scroller = viewport.querySelector('[data-cgt-cross-scroll]');
      if (!scroller) return;

      // Schritt = Breite einer Karte + Gap (~270 px, responsive robust)
      const firstCard = scroller.querySelector('.cgt-cross__card');
      const step = firstCard ? (firstCard.offsetWidth + 10) : 270;
      const delta = direction === 'prev' ? -step : step;

      scroller.scrollBy({ left: delta, behavior: 'smooth' });
      // Arrows nach Scroll neu einschaetzen
      setTimeout(() => this._updateCrossNavVisibility(viewport), 350);
    }

    _updateCrossNavVisibility(viewport) {
      const scroller = viewport.querySelector('[data-cgt-cross-scroll]');
      const prev = viewport.querySelector('[data-cgt-cross-nav="prev"]');
      const next = viewport.querySelector('[data-cgt-cross-nav="next"]');
      if (!scroller || !prev || !next) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      // Keine Overflow → gar keine Arrows
      if (maxScroll <= 2) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = scroller.scrollLeft <= 2;
      next.hidden = scroller.scrollLeft >= (maxScroll - 2);
    }

    _initCrossNav() {
      // Beim Drawer-Open pruefen wir alle Cross-Sell-Viewports auf Overflow
      document.querySelectorAll('[data-cross-sell] .cgt-cross__viewport').forEach((viewport) => {
        this._updateCrossNavVisibility(viewport);
        const scroller = viewport.querySelector('[data-cgt-cross-scroll]');
        if (scroller && !scroller._cgtScrollListener) {
          scroller._cgtScrollListener = true;
          scroller.addEventListener('scroll', () => {
            this._updateCrossNavVisibility(viewport);
          }, { passive: true });
        }
      });

      // v2.1.4+: Empty-State-Scroll Viewports (gleiche Logik)
      document.querySelectorAll('.cgt-empty__viewport').forEach((viewport) => {
        this._updateEmptyNavVisibility(viewport);
        const scroller = viewport.querySelector('[data-cgt-empty-scroll]');
        if (scroller && !scroller._cgtEmptyScrollListener) {
          scroller._cgtEmptyScrollListener = true;
          scroller.addEventListener('scroll', () => {
            this._updateEmptyNavVisibility(viewport);
          }, { passive: true });
        }
      });

      // v2.1.5+: Gift-Tiles-Scroll Viewports
      document.querySelectorAll('.cgt__tiles-viewport').forEach((viewport) => {
        this._updateTilesNavVisibility(viewport);
        const scroller = viewport.querySelector('[data-cgt-tiles-scroll]');
        if (scroller && !scroller._cgtTilesScrollListener) {
          scroller._cgtTilesScrollListener = true;
          scroller.addEventListener('scroll', () => {
            this._updateTilesNavVisibility(viewport);
          }, { passive: true });
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /*  GIFT-TILES NAV ARROWS (v2.1.5+)                                   */
    /* ------------------------------------------------------------------ */
    _handleTilesNav(btn) {
      const direction = btn.getAttribute('data-cgt-tiles-nav');
      const viewport = btn.closest('.cgt__tiles-viewport');
      if (!viewport) return;
      const scroller = viewport.querySelector('[data-cgt-tiles-scroll]');
      if (!scroller) return;

      const firstTile = scroller.querySelector('.cgt__tile');
      const step = firstTile ? (firstTile.offsetWidth + 8) : 240;
      const delta = direction === 'prev' ? -step : step;

      scroller.scrollBy({ left: delta, behavior: 'smooth' });
      setTimeout(() => this._updateTilesNavVisibility(viewport), 350);
    }

    _updateTilesNavVisibility(viewport) {
      const scroller = viewport.querySelector('[data-cgt-tiles-scroll]');
      const prev = viewport.querySelector('[data-cgt-tiles-nav="prev"]');
      const next = viewport.querySelector('[data-cgt-tiles-nav="next"]');
      if (!scroller || !prev || !next) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (maxScroll <= 2) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = scroller.scrollLeft <= 2;
      next.hidden = scroller.scrollLeft >= (maxScroll - 2);
    }

    /* ------------------------------------------------------------------ */
    /*  EMPTY-STATE NAV ARROWS (v2.1.4+)                                  */
    /* ------------------------------------------------------------------ */
    _handleEmptyNav(btn) {
      const direction = btn.getAttribute('data-cgt-empty-nav');
      const viewport = btn.closest('.cgt-empty__viewport');
      if (!viewport) return;
      const scroller = viewport.querySelector('[data-cgt-empty-scroll]');
      if (!scroller) return;

      // Schritt = Karten-Breite + Gap (Empty-Card ist 160px + 12px gap)
      const firstCard = scroller.querySelector('.cgt-empty__card');
      const step = firstCard ? (firstCard.offsetWidth + 12) : 172;
      const delta = direction === 'prev' ? -step : step;

      scroller.scrollBy({ left: delta, behavior: 'smooth' });
      setTimeout(() => this._updateEmptyNavVisibility(viewport), 350);
    }

    _updateEmptyNavVisibility(viewport) {
      const scroller = viewport.querySelector('[data-cgt-empty-scroll]');
      const prev = viewport.querySelector('[data-cgt-empty-nav="prev"]');
      const next = viewport.querySelector('[data-cgt-empty-nav="next"]');
      if (!scroller || !prev || !next) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (maxScroll <= 2) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = scroller.scrollLeft <= 2;
      next.hidden = scroller.scrollLeft >= (maxScroll - 2);
    }

    /* ------------------------------------------------------------------ */
    /*  CHECKOUT BUTTON LOADING                                           */
    /* ------------------------------------------------------------------ */
    _getCheckoutButtons() {
      return document.querySelectorAll(
        '.cart-drawer [slot="footer"] .buy-buttons .button, ' +
        '.cart-drawer [slot="footer"] button[name="checkout"], ' +
        '.cart-drawer [slot="footer"] [type="submit"], ' +
        '.cart-drawer [slot="footer"] a[href*="/checkout"]'
      );
    }

    _setCheckoutLoading(isLoading) {
      const btns = this._getCheckoutButtons();
      btns.forEach((btn) => {
        if (isLoading) {
          btn.classList.add('is-loading');
          btn.setAttribute('aria-busy', 'true');
          if (btn.tagName === 'BUTTON' || btn.tagName === 'INPUT') {
            btn.setAttribute('disabled', 'disabled');
          } else {
            btn.setAttribute('data-cgt-was-link', '1');
            btn.style.pointerEvents = 'none';
          }
        } else {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          if (btn.tagName === 'BUTTON' || btn.tagName === 'INPUT') {
            btn.removeAttribute('disabled');
          } else if (btn.getAttribute('data-cgt-was-link') === '1') {
            btn.removeAttribute('data-cgt-was-link');
            btn.style.pointerEvents = '';
          }
        }
      });
    }

    _attachCheckoutLoader() {
      // v2.5.9 HOTFIX: KOMPLETT DEAKTIVIERT.
      // Der alte globale capture-phase click-Listener hat auf dem Checkout-
      // Submit-Button e.preventDefault()+stopPropagation() gefeuert, sobald
      // `is-loading` noch gesetzt war (z.B. nach einem Gift-Add, wenn Impact
      // den Footer neu rendert und der alte is-loading-State noch im DOM
      // hängt). Ergebnis: "Sicher zur Kasse" klickt, Seite lädt kurz, dann
      // passiert nichts. Der Browser navigiert beim normalen Submit sowieso
      // weg — wir brauchen überhaupt keinen JS-Spinner-Hook.
      return;
    }

    /* ------------------------------------------------------------------ */
    /*  IMPACT-COMPATIBLE CART EVENT DISPATCH                             */
    /*  Impact erwartet event.detail = { cart, quantity, sections }       */
    /*  cart MUSS item_count haben (theme.js:1198)                        */
    /* ------------------------------------------------------------------ */
    _dispatchImpactCartChange(cart, quantity) {
      // HOTFIX v2.1.3: cart:change NUR dispatchen, wenn sections vorhanden sind.
      // Ohne sections crasht Impact's _onCartChanged beim Zugriff auf
      // event.detail.sections['sections--XXX__cart-drawer'] (TypeError).
      // Fallback: nur cart:refresh → Impact refetched intern sauber.
      if (cart && cart.sections) {
        const detail = { cart: cart, quantity: quantity, sections: cart.sections };
        document.dispatchEvent(new CustomEvent('cart:change', { detail, bubbles: true }));
      } else {
        LOG('dispatch: no sections, using cart:refresh fallback');
      }
      // cart:refresh als zusaetzlicher Trigger fuer Impact
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    _changeLineItem(key, quantity) {
      const sections = this._getBundledSections();
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key, quantity: quantity, sections: sections, sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json());
    }

    _getBundledSections() {
      const event = new CustomEvent('cart:prepare-bundled-sections', {
        detail: { sections: [] }, bubbles: true,
      });
      document.dispatchEvent(event);
      return event.detail.sections.join(',');
    }

    _refreshCart() {
      // HOTFIX v2.1.3: /cart.js liefert KEIN sections-Feld (GET-Endpoint).
      // Wir POSTen daher an /cart/update.js mit sections-Parameter → das
      // liefert sowohl frischen Cart ALS AUCH die gerenderten Sections.
      const sections = this._getBundledSections();
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((cart) => this._dispatchImpactCartChange(cart, 0))
        .catch((e) => WARN('refreshCart error', e));
    }

    /* ------------------------------------------------------------------ */
    /*  CONFETTI                                                          */
    /* ------------------------------------------------------------------ */
    _fireConfetti() {
      // v2.5.10: Nur feuern wenn Cart-Drawer tatsaechlich offen ist
      const drawer = this.closest('cart-drawer');
      if (!drawer || !drawer.hasAttribute('open')) return;

      this._loadConfetti().then((confetti) => {
        if (!confetti) return;
        const rect = this.getBoundingClientRect();
        const x = (rect.left + rect.width / 2) / window.innerWidth;
        const y = (rect.top + rect.height / 2) / window.innerHeight;
        confetti({
          particleCount: 90, spread: 75, startVelocity: 38,
          origin: { x, y }, zIndex: 10000, disableForReducedMotion: true,
          colors: ['#8E63F8', '#5B2EE0', '#C4A7FF', '#FFFFFF'],
        });
      });
    }

    _loadConfetti() {
      if (this._confettiLib) return Promise.resolve(this._confettiLib);
      if (window.confetti) { this._confettiLib = window.confetti; return Promise.resolve(this._confettiLib); }
      return new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
        s.async = true;
        s.onload = () => { this._confettiLib = window.confetti; resolve(this._confettiLib); };
        s.onerror = () => resolve(null);
        document.head.appendChild(s);
      });
    }
  }

  if (!customElements.get('cart-gift-tiers')) {
    customElements.define('cart-gift-tiers', CartGiftTiers);
  }
})();